import { Controller } from "@hotwired/stimulus"
import { readCaptureMetadata } from "../lib/exif_reader"
import { showToast } from "../lib/toast"
import { UPLOAD_LIMIT_BYTES, showSizeLimitModal } from "../lib/size_limit_modal"

// One presigned PUT takes a video up to 500MB (the server's
// PresignableUploads::VIDEO_MAX_UPLOAD_SIZE). Bigger videos go straight to
// S3 in ~100MB parts, like Documents (Dorothy Shupe, 2026-10-05: a
// walk-through MP4 "took the download but then said it failed"), up to the
// 5 GB upload limit — past that, a pop-up says how to get it raised.
const SINGLE_PUT_VIDEO_MAX = 500 * 1024 * 1024

export default class extends Controller {
  static targets = [
    "grid", "sentinel", "uploadModal", "uploadDropzone", "uploadFileInput",
    "uploadFileList", "uploadSaveBtn", "sourceFilter", "dateFrom", "dateTo",
    "dateLabel", "sortLabel", "searchInput", "photoCount", "bulkBar", "bulkCount", "bulkToggle", "empty",
    "downloadBtn", "downloadBtnContent", "downloadBtnSpinner",
    "deleteBtn", "deleteConfirmModal", "deleteConfirmMessage", "selectAllCheckbox"
  ]

  static values = {
    projectId: Number,
    currentUserId: Number,
    projectNumber: String,
    projectName: String,
    presignUrl: String,
    registerUrl: String,
    manualUploadsUrl: String,
    presignMultipartUrl: String,
    completeMultipartUrl: String,
    abortMultipartUrl: String,
    sizeLimitText: Object,
    projectPhase: String,
    photosUrl: String,
    baseUrl: String,
    sort: String,
    saveSortUrl: String,
    pollThumbnailsUrl: String,
    heicWorkerUrl: String
  }

  connect() {
    this._page = 1
    this._loading = false
    this._isLastPage = false
    this._allPhotos = []
    this._selectedPhotos = new Set()
    this._ownedPhotoIds = new Set()  // tracks which photos the current user owns
    this._bulkMode = false
    this._weekPhotos = new Map()
    this._searchTimeout = null
    this._lastWeekKey = null
    // Seed from the user's saved preference (persisted per user, all projects).
    this._sort = this.sortValue || "recent"

    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._loading && !this._isLastPage) {
          this._loadPage(this._page)
        }
      },
      { rootMargin: "300px", threshold: 0 }
    )

    if (this.hasSentinelTarget) {
      this._observer.observe(this.sentinelTarget)
    }

    this._loadPage(1)
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    if (this._heicWorker) {
      this._heicWorker.terminate()
      this._heicWorker = null
      this._heicCallbacks = {}
    }
  }

  // ── Data loading ──────────────────────────────────────────────────

  async _loadPage(page) {
    if (this._loading || this._isLastPage) return
    this._loading = true

    const url = new URL(this.photosUrlValue, window.location.origin)
    url.searchParams.set("page", page)

    if (this.hasSourceFilterTarget && this.sourceFilterTarget.value) {
      url.searchParams.set("source", this.sourceFilterTarget.value)
    }
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) {
      url.searchParams.set("search", this.searchInputTarget.value.trim())
    }
    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      url.searchParams.set("start_date", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      url.searchParams.set("end_date", this.dateToTarget.value)
    }
    if (this._sort && this._sort !== "recent") {
      url.searchParams.set("sort", this._sort)
    }

    try {
      const res = await fetch(url, { headers: { "Accept": "application/json" } })
      const data = await res.json()

      this._renderPhotos(data.photos)
      this._isLastPage = data.is_last_page
      this._page = page + 1

      if (this.hasPhotoCountTarget) {
        this.photoCountTarget.textContent = data.total_count
      }

      // Show/hide empty state
      if (page === 1 && data.photos.length === 0) {
        if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
        if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
      } else {
        if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
      }

      if (this._isLastPage && this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden")
      }

      // If viewport not filled, try loading more
      if (!this._isLastPage && data.photos.length > 0) {
        requestAnimationFrame(() => {
          if (this.hasSentinelTarget) {
            const rect = this.sentinelTarget.getBoundingClientRect()
            if (rect.top < window.innerHeight + 300) {
              this._loadPage(this._page)
            }
          }
        })
      }
    } catch (e) {
      console.error("Failed to load photos:", e)
    } finally {
      this._loading = false
    }
  }

  // ── Grid rendering ────────────────────────────────────────────────

  _renderPhotos(photos) {
    photos.forEach(photo => {
      this._allPhotos.push(photo)
      const groupDate = this._groupDateFor(photo)
      const weekKey = this._weekKeyFromDate(groupDate)

      // Track photo in week map
      if (!this._weekPhotos.has(weekKey)) this._weekPhotos.set(weekKey, new Set())
      this._weekPhotos.get(weekKey).add(photo.id)

      // Date section headers on EVERY sort — grouped by the sort's own date
      // (upload / taken / last comment) so the sections always read in order.
      if (weekKey !== this._lastWeekKey) {
        this._lastWeekKey = weekKey
        const header = this._createWeekHeader(groupDate, weekKey)
        this.gridTarget.appendChild(header)
      }

      const tile = this._createPhotoTile(photo)
      this.gridTarget.appendChild(tile)
    })

    // Update checkbox states if bulk mode is active (handles new photos from infinite scroll)
    if (this._bulkMode && photos.length > 0) {
      this._updateBulkCount()
    }
  }

  // The date a photo is grouped under, matching the active sort. Prefers the
  // server-provided `group_date`; falls back to the best available field (for
  // JSON payloads like fresh manual uploads that don't include it).
  _groupDateFor(photo) {
    // Server sends group_date for the active sort; null means "no date for this
    // sort" → a trailing "No date" section. Only fall back when the field is
    // absent entirely (e.g. fresh manual uploads that predate this payload).
    if ("group_date" in photo) return photo.group_date
    if (this._sort === "taken") return photo.taken_at || photo.created_at
    return photo.created_at
  }

  // Stable key for the Monday–Sunday week containing `dateStr` (the Monday's
  // date). Used for both the section headers and the bulk "select week" map.
  // Null/blank dates bucket into a single "undated" group.
  _weekKeyFromDate(dateStr) {
    if (!dateStr) return "undated"
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return "undated"
    const day = d.getDay()
    const monday = new Date(d)
    monday.setDate(d.getDate() - ((day + 6) % 7))
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`
  }

  _createWeekHeader(dateStr, weekKey) {
    let text
    if (weekKey === "undated" || !dateStr) {
      text = this._sort === "commented" ? "No comments yet" : "No date taken"
    } else {
      const date = new Date(dateStr)
      const day = date.getDay()
      const monday = new Date(date)
      monday.setDate(date.getDate() - ((day + 6) % 7))
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)

      const lang = document.documentElement.lang || "en"
      const fmt = { month: "short", day: "numeric", year: "numeric" }
      text = `${monday.toLocaleDateString(lang, fmt)} \u2013 ${sunday.toLocaleDateString(lang, fmt)}`
    }

    const el = document.createElement("div")
    el.className = "col-span-full py-2.5 px-1 font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 flex items-center gap-2"
    el.style.fontSize = "13px"
    el.dataset.weekKey = weekKey

    // Week select checkbox (visible only in bulk mode)
    const cb = document.createElement("input")
    cb.type = "checkbox"
    cb.className = "form-checkbox h-3.5 w-3.5 text-blue-500 rounded border-gray-300 cursor-pointer shrink-0"
    cb.dataset.weekCheckbox = weekKey
    if (!this._bulkMode) cb.style.display = "none"
    cb.addEventListener("click", (e) => e.stopPropagation())
    cb.addEventListener("change", () => this._toggleWeekSelect(weekKey))
    el.appendChild(cb)

    const span = document.createElement("span")
    span.textContent = text
    el.appendChild(span)

    // Click header text to toggle week selection in bulk mode
    if (this._bulkMode) el.style.cursor = "pointer"
    el.addEventListener("click", (e) => {
      if (this._bulkMode && e.target !== cb) {
        cb.checked = !cb.checked
        cb.indeterminate = false
        this._toggleWeekSelect(weekKey)
      }
    })

    return el
  }

  _createPhotoTile(photo) {
    const div = document.createElement("div")
    div.className = "aspect-square bg-gray-100 relative group cursor-pointer overflow-hidden"
    div.dataset.photoId = photo.id

    // Container for overlays (badges, checkbox) — hidden until image loads
    const overlays = document.createElement("div")
    overlays.className = "contents"
    overlays.dataset.role = "overlays"

    const tileUrl = photo.thumbnail_url || photo.original_url
    if (photo.is_video) {
      div.classList.remove("bg-gray-100")
      div.classList.add("bg-gray-900")
      if (photo.thumbnail_url) this._addVideoStill(div, photo.thumbnail_url)
      const play = document.createElement("div")
      play.className = "absolute inset-0 flex items-center justify-center text-white/90"
      play.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-12 h-12 drop-shadow"><path d="M8 5v14l11-7z"/></svg>`
      div.appendChild(play)

      if (photo.duration) {
        const dur = document.createElement("div")
        dur.setAttribute("data-role", "duration-badge")
        dur.className = "absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-black/70 text-white text-[13px] rounded tabular-nums"
        dur.textContent = this._formatDuration(photo.duration)
        overlays.appendChild(dur)
      }
    } else if (tileUrl && photo.is_image) {
      div.classList.add("animate-pulse")
      overlays.style.display = "none"

      const bg = document.createElement("div")
      bg.className = "absolute inset-0 bg-cover bg-center opacity-0 transition-opacity duration-300"
      bg.dataset.role = "thumbnail-bg"
      if (photo.rotation) bg.style.transform = `rotate(${photo.rotation}deg) scale(1.2)`
      div.appendChild(bg)

      // Preload then reveal
      const img = new Image()
      img.onload = () => {
        bg.style.backgroundImage = `url(${img.src})`
        bg.classList.replace("opacity-0", "opacity-100")
        div.classList.remove("animate-pulse")
        overlays.style.display = ""
      }
      img.onerror = () => {
        // Thumbnail version missing — fall back to original
        if (photo.original_url && img.src !== photo.original_url) {
          img.src = photo.original_url
          return
        }
        // Both failed — show placeholder
        div.classList.remove("animate-pulse")
        div.classList.add("border", "border-gray-200")
        div.classList.add("photo-thumb-bg")
        bg.remove()
        div.insertAdjacentHTML("afterbegin", `<div class="absolute inset-0 flex items-center justify-center text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10">
            <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Zm13.5-9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
          </svg>
        </div>`)
        overlays.style.display = ""
      }
      img.src = tileUrl
    } else {
      // Placeholder photo icon (matches project card covers)
      div.classList.add("border", "border-gray-200")
      div.classList.add("photo-thumb-bg")
      div.innerHTML = `<div class="absolute inset-0 flex items-center justify-center text-gray-300">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10">
          <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Zm13.5-9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
        </svg>
      </div>`
    }

    // Hover overlay
    const overlay = document.createElement("div")
    overlay.className = "absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"
    overlays.appendChild(overlay)

    // Track ownership for permission-aware bulk actions
    const isOwn = (photo.owner_id || photo.user_id) === this.currentUserIdValue
    if (isOwn) this._ownedPhotoIds.add(photo.id)

    // Checkbox for bulk mode (all photos are selectable)
    const checkbox = document.createElement("div")
    checkbox.className = `absolute top-1.5 left-1.5 z-10 ${this._bulkMode ? "" : "hidden"} transition-opacity`
    checkbox.innerHTML = `<input type="checkbox" class="form-checkbox h-4 w-4 text-blue-500 rounded border-2 border-white shadow"
                                  data-action="click->photos#toggleSelect"
                                  data-photo-id="${photo.id}"
                                  ${this._selectedPhotos.has(photo.id) ? "checked" : ""}>`
    overlays.appendChild(checkbox)

    // Comment count badge
    if (photo.comment_count > 0) {
      const badge = document.createElement("div")
      badge.setAttribute("data-role", "comment-badge")
      badge.className = "absolute bottom-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/60 text-white text-xs rounded"
      badge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" /></svg>${photo.comment_count}`
      overlays.appendChild(badge)
    }

    // Annotation badge
    if (photo.has_annotations) {
      const annBadge = document.createElement("div")
      annBadge.setAttribute("data-role", "annotation-badge")
      annBadge.className = "absolute bottom-1.5 right-1.5 p-1 bg-brand/80 text-white rounded"
      annBadge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128Zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42"/></svg>`
      overlays.appendChild(annBadge)
    }

    div.appendChild(overlays)

    // Click handler
    div.addEventListener("click", (e) => {
      if (e.target.type === "checkbox") return
      if (this._bulkMode) {
        const cb = div.querySelector('input[type="checkbox"]')
        if (cb) { cb.checked = !cb.checked; this._updateSelection(photo.id, cb.checked) }
      } else {
        this._openLightbox(photo.id)
      }
    })

    return div
  }

  // ── Lightbox ──────────────────────────────────────────────────────

  _openLightbox(photoId) {
    const index = this._allPhotos.findIndex(p => p.id === photoId)
    if (index < 0) return

    const lightboxCtrl = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="photo-lightbox"]'),
      "photo-lightbox"
    )
    if (lightboxCtrl) {
      lightboxCtrl.open({ photos: this._allPhotos, index })
    }
  }

  // ── Upload modal ──────────────────────────────────────────────────

  openUploadModal() {
    this._uploadFiles = []
    this._eagerUploadPromises = []
    if (this.hasUploadFileListTarget) this.uploadFileListTarget.innerHTML = ""
    if (this.hasUploadFileInputTarget) this.uploadFileInputTarget.value = ""
    this.uploadModalTarget.classList.remove("hidden")
  }

  closeUploadModal() {
    // Revoke blob URLs to prevent memory leaks
    if (this._uploadFiles) {
      this._uploadFiles.forEach(f => {
        if (f?._uploadResult?._blobUrl) URL.revokeObjectURL(f._uploadResult._blobUrl)
        // A big video still going up when the box closes unsaved is cancelled.
        if (f && !f._bigVideoResult) f._cancelled = true
      })
    }
    this.uploadModalTarget.classList.add("hidden")
    this._uploadFiles = []
    this._eagerUploadPromises = []
  }

  triggerFileInput() {
    if (this.hasUploadFileInputTarget) this.uploadFileInputTarget.click()
  }

  dragOver(e) {
    e.preventDefault()
    if (this.hasUploadDropzoneTarget) this.uploadDropzoneTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeave(e) {
    e.preventDefault()
    if (this.hasUploadDropzoneTarget) this.uploadDropzoneTarget.classList.remove("border-brand", "bg-brand/5")
  }

  dropFiles(e) {
    e.preventDefault()
    if (this.hasUploadDropzoneTarget) this.uploadDropzoneTarget.classList.remove("border-brand", "bg-brand/5")
    if (e.dataTransfer.files.length) this._addUploadFiles(e.dataTransfer.files)
  }

  handleFiles(e) {
    if (e.target.files.length) this._addUploadFiles(e.target.files)
  }

  _addUploadFiles(fileList) {
    if (!this._uploadFiles) this._uploadFiles = []

    // Over the 5 GB limit: the pop-up says so and how to get it raised, and
    // the file isn't added.
    const files = Array.from(fileList)
    const tooBig = files.find(f => f.size > UPLOAD_LIMIT_BYTES)
    if (tooBig) showSizeLimitModal(tooBig, this.sizeLimitTextValue, { source: "photos", project_id: this.projectIdValue, project_phase: this.projectPhaseValue })

    const newEntries = []
    files.filter(f => f.size <= UPLOAD_LIMIT_BYTES).forEach(file => {
      const entry = { file, comment: "", convertedBlob: null, duration: null, _uploadResult: null }
      this._uploadFiles.push(entry)
      const index = this._uploadFiles.length - 1
      this._renderUploadFileRow(index, file)
      newEntries.push({ entry, index })

      // Eagerly convert HEIC → JPEG in a Web Worker (keeps UI responsive)
      if (this._isHeic(file)) {
        entry._heicPromise = this._convertHeic(file, index, entry)
      } else if (this._isVideo(file)) {
        entry._durationPromise = this._extractVideoDuration(file, entry)
      }

      // Read EXIF (taken-at + GPS) from the ORIGINAL file — compression
      // re-encodes through canvas and strips it
      if (!this._isVideo(file)) {
        entry._exifPromise = readCaptureMetadata(file)
          .then(meta => { entry.captureMeta = meta })
          .catch(() => {})
      }
    })

    // Start uploading to S3 immediately (compress + presign + PUT). Big
    // videos go up in parts on their own; Save waits for all of it.
    const big = newEntries.filter(({ entry }) => this._isBigVideo(entry.file))
    const regular = newEntries.filter(({ entry }) => !this._isBigVideo(entry.file))
    if (regular.length) this._trackUpload(this._eagerUploadBatch(regular))
    big.forEach(({ entry, index }) => this._trackUpload(this._uploadBigVideo(entry, index)))
  }

  _trackUpload(promise) {
    if (!this._eagerUploadPromises) this._eagerUploadPromises = []
    this._eagerUploadPromises.push(promise.catch(() => {}))
  }

  _isBigVideo(file) {
    return this._isVideo(file) && file.size > SINGLE_PUT_VIDEO_MAX
  }

  // A video past one upload's 500MB: presign (the server creates the photo,
  // hidden), PUT ~100MB parts with the row's progress bar, then complete.
  // A failed or cancelled upload is aborted so nothing half-done stays.
  async _uploadBigVideo(entry, index) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = { "X-CSRF-Token": token, "Content-Type": "application/json" }
    const file = entry.file

    let session = null
    try {
      const res = await fetch(this.presignMultipartUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size })
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Couldn't start the upload")
      session = await res.json()

      const bar = this._showUploadProgress(index)
      let sent = 0
      for (const part of session.part_urls) {
        if (entry._cancelled) throw new Error("cancelled")
        const start = (part.part_number - 1) * session.part_size
        const blob = file.slice(start, Math.min(start + session.part_size, file.size))
        await this._putPart(part.url, blob, loaded => {
          if (bar) bar.style.width = `${Math.round(((sent + loaded) / file.size) * 100)}%`
        })
        sent += blob.size
      }

      if (entry._durationPromise) await entry._durationPromise
      const done = await fetch(this.completeMultipartUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ attachment_id: session.attachment_id, upload_id: session.upload_id,
                               expected_parts: session.part_urls.length, duration: entry.duration })
      })
      if (!done.ok) throw new Error((await done.json().catch(() => ({}))).error || "Couldn't finish the upload")
      entry._bigVideoResult = { photo_id: session.photo_id }
    } catch (e) {
      if (session) {
        fetch(this.abortMultipartUrlValue, {
          method: "POST", headers, credentials: "same-origin",
          body: JSON.stringify({ attachment_id: session.attachment_id, upload_id: session.upload_id })
        }).catch(() => {})
      }
      if (!entry._cancelled) this._markUploadFailed(index, entry, e.message)
    }
  }

  _putPart(url, blob, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded) }
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)))
      xhr.onerror = () => reject(new Error("Connection lost during the upload"))
      xhr.open("PUT", url)
      xhr.send(blob)
    })
  }

  _showUploadProgress(index) {
    const wrap = this.uploadFileListTarget.querySelector(`[data-upload-index="${index}"] [data-progress-bar]`)
    if (!wrap) return null
    wrap.classList.remove("hidden")
    return wrap.querySelector(".bg-brand")
  }

  // Says on the file's row why it didn't go up (it used to just vanish).
  _markUploadFailed(index, entry, message) {
    entry._uploadError = message
    const row = this.uploadFileListTarget.querySelector(`[data-upload-index="${index}"]`)
    if (!row) return
    row.querySelector("[data-progress-bar]")?.classList.add("hidden")
    let note = row.querySelector("[data-upload-error]")
    if (!note) {
      note = document.createElement("p")
      note.className = "mt-1 text-[13px] text-red-600"
      note.dataset.uploadError = ""
      row.querySelector(".flex-1")?.appendChild(note)
    }
    note.textContent = message
  }

  _isHeic(file) {
    const type = file.type.toLowerCase()
    if (type.includes("heic") || type.includes("heif")) return true
    const ext = file.name.split(".").pop().toLowerCase()
    return ext === "heic" || ext === "heif"
  }

  _isVideo(file) {
    const type = (file.type || "").toLowerCase()
    if (type.startsWith("video/")) return true
    const ext = file.name.split(".").pop().toLowerCase()
    return ["mp4", "mov", "webm"].includes(ext)
  }

  _extractVideoDuration(file, entry) {
    // Reads HTMLVideoElement.duration via a blob URL, then releases the object URL.
    // Resolves to null on failure — backend will just store null duration.
    return new Promise((resolve) => {
      const video = document.createElement("video")
      video.preload = "metadata"
      const blobUrl = URL.createObjectURL(file)
      const cleanup = () => { URL.revokeObjectURL(blobUrl); video.remove() }
      video.onloadedmetadata = () => {
        const secs = Number.isFinite(video.duration) ? video.duration : null
        entry.duration = secs
        cleanup()
        resolve(secs)
      }
      video.onerror = () => { cleanup(); resolve(null) }
      video.src = blobUrl
    })
  }

  async _convertHeic(file, index, entry) {
    const row = this.uploadFileListTarget.querySelector(`[data-upload-index="${index}"]`)
    const thumbDiv = row?.querySelector("[data-role='upload-thumb']")

    try {
      // Decode HEIC in a Web Worker to keep UI responsive
      if (!this._heicWorker) {
        this._heicWorker = new Worker(this.heicWorkerUrlValue)
        this._heicCallbacks = {}
        this._heicWorker.onmessage = (e) => {
          const cb = this._heicCallbacks[e.data.id]
          if (cb) {
            delete this._heicCallbacks[e.data.id]
            if (e.data.error) cb.reject(new Error(e.data.error))
            else cb.resolve(e.data)
          }
        }
        this._heicWorker.onerror = (e) => {
          console.error("HEIC worker error:", e)
          for (const [cbId, cb] of Object.entries(this._heicCallbacks)) {
            cb.reject(new Error("Worker error"))
            delete this._heicCallbacks[cbId]
          }
        }
      }

      const buffer = await file.arrayBuffer()
      const id = `heic_${Date.now()}_${index}`

      const result = await new Promise((resolve, reject) => {
        this._heicCallbacks[id] = { resolve, reject }
        this._heicWorker.postMessage({ id, buffer }, [buffer])
      })

      // Convert raw RGBA from worker → JPEG blob via canvas (main thread, instant)
      const canvas = document.createElement("canvas")
      canvas.width = result.width
      canvas.height = result.height
      const ctx = canvas.getContext("2d")
      const imageData = new ImageData(
        new Uint8ClampedArray(result.data),
        result.width,
        result.height
      )
      ctx.putImageData(imageData, 0, 0)

      const converted = await new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), "image/jpeg", 0.8)
      })

      entry.convertedBlob = converted

      // Update modal thumbnail
      if (thumbDiv) {
        const blobUrl = URL.createObjectURL(converted)
        thumbDiv.innerHTML = `<img src="${blobUrl}" class="w-full h-full object-cover">`
      }
    } catch (e) {
      console.warn("HEIC conversion failed, server will handle it:", e)
      // Show placeholder — upload still works, server converts via CarrierWave
      if (thumbDiv) {
        thumbDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/></svg>`
      }
    }
  }

  _renderUploadFileRow(index, file) {
    const row = document.createElement("div")
    row.className = "flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
    row.dataset.uploadIndex = index

    // Thumbnail
    const thumbDiv = document.createElement("div")
    thumbDiv.className = "w-16 h-16 rounded bg-gray-200 shrink-0 overflow-hidden flex items-center justify-center"
    thumbDiv.dataset.role = "upload-thumb"
    if (file.type.startsWith("image/") || this._isHeic(file)) {
      if (this._isHeic(file)) {
        // Show spinner while HEIC converts
        thumbDiv.innerHTML = `<svg class="w-5 h-5 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
      } else {
        const img = document.createElement("img")
        img.className = "w-full h-full object-cover"
        const blobUrl = URL.createObjectURL(file)
        img.src = blobUrl
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl)
          img.remove()
          thumbDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/></svg>`
        }
        thumbDiv.appendChild(img)
      }
    } else if (this._isVideo(file)) {
      thumbDiv.classList.add("bg-gray-800", "text-white")
      thumbDiv.classList.remove("bg-gray-200")
      thumbDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6"><path d="M8 5v14l11-7z"/></svg>`
    } else {
      thumbDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>`
    }
    row.appendChild(thumbDiv)

    // Info + comment
    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    const nameP = document.createElement("p")
    nameP.className = "text-sm font-medium text-gray-700 truncate"
    nameP.textContent = file.name
    info.appendChild(nameP)
    info.insertAdjacentHTML("beforeend", `
      <p class="text-xs text-gray-400">${this._formatFileSize(file.size)}</p>
      <input type="text" placeholder="Add comment..." data-upload-index="${index}"
             data-action="input->photos#updateUploadComment"
             class="mt-1.5 w-full px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-brand focus:border-brand">
      <div class="mt-1.5 hidden" data-progress-bar>
        <div class="w-full bg-gray-200 rounded-full h-1">
          <div class="bg-brand h-1 rounded-full transition-all" style="width: 0%"></div>
        </div>
      </div>
    `)
    row.appendChild(info)

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.className = "p-1 text-gray-400 hover:text-gray-600 shrink-0"
    removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`
    removeBtn.addEventListener("click", () => {
      if (this._uploadFiles[index]) this._uploadFiles[index]._cancelled = true // stops a big video mid-upload
      this._uploadFiles[index] = null
      row.remove()
    })
    row.appendChild(removeBtn)

    this.uploadFileListTarget.appendChild(row)
  }

  updateUploadComment(e) {
    const idx = parseInt(e.target.dataset.uploadIndex)
    if (this._uploadFiles[idx]) this._uploadFiles[idx].comment = e.target.value
  }

  async _eagerUploadBatch(entries) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // Presign using original file metadata (doesn't need compressed size)
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: entries.map(({ entry }) => ({
            filename: entry.file.name,
            content_type: entry.file.type || "application/octet-stream",
            byte_size: entry.file.size
          }))
        })
      })

      if (!presignRes.ok) {
        // The server refuses the whole batch over one file (type or size), so
        // retry one by one: the rest still go up and the bad one says why.
        if (entries.length > 1) {
          await Promise.all(entries.map(e => this._eagerUploadBatch([e])))
          return
        }
        const err = await presignRes.json().catch(() => ({}))
        this._markUploadFailed(entries[0].index, entries[0].entry, err.error || "Couldn't start the upload")
        return
      }

      const { uploads } = await presignRes.json()

      // Pipeline: compress each file then PUT to S3 as soon as it's ready
      const uploadPromises = entries.map(async ({ entry, index }, i) => {
        const upload = uploads[i]
        const prepared = await this._prepareFile(entry)

        const res = await fetch(upload.upload_url, {
          method: "PUT",
          headers: { "Content-Type": prepared.blob.type || upload.content_type },
          body: prepared.blob
        })
        if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)

        entry._uploadResult = {
          cache_name: upload.cache_name,
          filename: upload.filename,
          _blobUrl: URL.createObjectURL(prepared.blob)
        }

      }).map((p, i) => p.catch(e => this._markUploadFailed(entries[i].index, entries[i].entry, e.message)))

      const batchPromise = Promise.allSettled(uploadPromises)
      if (!this._eagerUploadPromises) this._eagerUploadPromises = []
      this._eagerUploadPromises.push(batchPromise)
      await batchPromise
    } catch (e) {
      console.error("Eager upload batch failed:", e)
    }
  }

  async saveUploads() {
    const files = (this._uploadFiles || []).filter(f => f !== null)
    if (files.length === 0) return

    const saveBtn = this.uploadSaveBtnTarget
    const savedLabel = saveBtn.textContent
    saveBtn.disabled = true
    saveBtn.classList.add("opacity-50", "cursor-not-allowed")
    saveBtn.innerHTML = `<svg class="inline w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // Wait for any still-uploading files (usually already done by now)
      if (this._eagerUploadPromises) {
        await Promise.all(this._eagerUploadPromises)
      }

      // Wait for any in-flight video duration extractions (usually already done)
      await Promise.all(files.map(f => f._durationPromise).filter(Boolean))

      // Wait for EXIF reads (in practice instant — file headers only)
      await Promise.all(files.map(f => f._exifPromise).filter(Boolean))

      const successful = files
        .filter(f => f._uploadResult)
        .map((f, i) => ({
          ...f._uploadResult,
          _comment: f.comment,
          duration: f.duration,
          taken_at: f.captureMeta?.taken_at ?? null,
          latitude: f.captureMeta?.latitude ?? null,
          longitude: f.captureMeta?.longitude ?? null,
          altitude: f.captureMeta?.altitude ?? null,
          heading: f.captureMeta?.heading ?? null,
          camera_make: f.captureMeta?.camera_make ?? null,
          camera_model: f.captureMeta?.camera_model ?? null,
          exif: f.captureMeta?.exif ?? null
        }))
      // Big videos are registered already (the server made the photo when
      // the upload started); they only still need to join the project.
      const bigVideos = files
        .filter(f => f._bigVideoResult)
        .map(f => ({ photo_id: f._bigVideoResult.photo_id, _comment: f.comment, _blobUrl: URL.createObjectURL(f.file) }))
      const failed = files.filter(f => !f._uploadResult && !f._bigVideoResult)
      if (successful.length === 0 && bigVideos.length === 0) {
        throw new Error(failed.map(f => f._uploadError).find(Boolean) || "All uploads failed")
      }

      // Clear blob URL refs from saved entries so closeUploadModal() won't revoke them
      // (they're used as temporary grid thumbnails until S3 versions are ready)
      files.filter(f => f._uploadResult).forEach(f => { f._uploadResult._blobUrl = null })

      // Close immediately — S3 uploads are already done
      this.closeUploadModal()

      // Register + associate in background
      this._finishUpload(successful, bigVideos, token)
      if (failed.length > 0) {
        showToast(failed.map(f => `${f.file.name}: ${f._uploadError || "couldn't upload"}`).join(" · "), "error")
      }
    } catch (e) {
      console.error("Upload failed:", e)
      showToast(`Upload failed: ${e.message}`, "error")
    } finally {
      saveBtn.disabled = false
      saveBtn.classList.remove("opacity-50", "cursor-not-allowed")
      saveBtn.textContent = savedLabel
    }
  }

  async _finishUpload(successful, bigVideos, token) {
    try {
      // Register with server
      let registered = []
      if (successful.length > 0) {
        const registerRes = await fetch(this.registerUrlValue, {
          method: "POST",
          headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
          body: JSON.stringify({
            files: successful.map(u => ({
              cache_name: u.cache_name,
              filename: u.filename,
              duration: u.duration,
              taken_at: u.taken_at,
              latitude: u.latitude,
              longitude: u.longitude,
              altitude: u.altitude,
              heading: u.heading,
              camera_make: u.camera_make,
              camera_model: u.camera_model,
              exif: u.exif
            }))
          })
        })
        registered = await registerRes.json()
      }

      const items = [
        ...registered.map((a, i) => ({ photo_id: a.photo_id, comment: successful[i]?._comment || "", blobUrl: successful[i]?._blobUrl })),
        ...bigVideos.map(v => ({ photo_id: v.photo_id, comment: v._comment || "", blobUrl: v._blobUrl }))
      ]

      // Associate photos with project + set comments
      const manualRes = await fetch(this.manualUploadsUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          photos_data: items.map(({ photo_id, comment }) => ({ photo_id, comment }))
        })
      })

      const manualData = await manualRes.json()

      // Prepend new photos to grid, using client-side blobs as temporary
      // thumbnails until StoreAssetJob generates the real S3 versions
      if (manualData.photos && manualData.photos.length > 0) {
        manualData.photos.forEach((photo, i) => {
          const blobUrl = items[i]?.blobUrl
          if (!blobUrl) return
          if (photo.is_video) {
            // Videos keep the dark-tile grid rendering; blob URL is only useful
            // for inline playback in the lightbox until S3 is ready.
            if (!photo.original_url) photo.original_url = blobUrl
            if (!photo.optimized_url) photo.optimized_url = blobUrl
          } else {
            photo.thumbnail_url = blobUrl
            photo.optimized_url = blobUrl
            if (!photo.original_url) photo.original_url = blobUrl
          }
        })
        this._prependPhotos(manualData.photos)
        this._pollForThumbnails(manualData.photos.map(p => p.id))
      }
    } catch (e) {
      console.error("Background upload registration failed:", e)
    }
  }

  _prependPhotos(photos) {
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    if (this.hasSentinelTarget) this.sentinelTarget.classList.remove("hidden")

    // Group new photos by week key (matching the active sort's date)
    const byWeek = new Map()
    photos.forEach(photo => {
      const key = this._weekKeyFromDate(this._groupDateFor(photo))
      if (!byWeek.has(key)) byWeek.set(key, [])
      byWeek.get(key).push(photo)
    })

    // Insert each group into its matching week section (or create a new one)
    for (const [weekKey, weekPhotos] of byWeek) {
      let header = this.gridTarget.querySelector(`[data-week-key="${weekKey}"]`)

      if (!header) {
        // Create a new week header and insert at the top of the grid
        header = this._createWeekHeader(this._groupDateFor(weekPhotos[0]), weekKey)
        const firstChild = this.gridTarget.firstElementChild
        if (firstChild) {
          this.gridTarget.insertBefore(header, firstChild)
        } else {
          this.gridTarget.appendChild(header)
        }
      }

      // Find the insertion point: right after the header
      const insertBefore = header.nextElementSibling

      weekPhotos.forEach(photo => {
        this._allPhotos.unshift(photo)
        // Track in week map
        if (!this._weekPhotos.has(weekKey)) this._weekPhotos.set(weekKey, new Set())
        this._weekPhotos.get(weekKey).add(photo.id)
        const tile = this._createPhotoTile(photo)
        if (insertBefore) {
          this.gridTarget.insertBefore(tile, insertBefore)
        } else {
          this.gridTarget.appendChild(tile)
        }
      })
    }

    // Update count
    if (this.hasPhotoCountTarget) {
      const current = parseInt(this.photoCountTarget.textContent) || 0
      this.photoCountTarget.textContent = current + photos.length
    }
  }

  // The still the server takes from a video (Phelipe 2026-10-05), under
  // the play button; shown once it has loaded, so a missing one leaves the
  // dark tile as it was.
  _addVideoStill(tile, url) {
    let still = tile.querySelector('[data-role="video-still"]')
    if (!still) {
      still = document.createElement("div")
      still.className = "absolute inset-0 bg-cover bg-center opacity-0 transition-opacity duration-300"
      still.dataset.role = "video-still"
      tile.prepend(still)
    }
    const img = new Image()
    img.onload = () => {
      still.style.backgroundImage = `url("${url}")`
      still.classList.replace("opacity-0", "opacity-100")
    }
    img.onerror = () => still.remove()
    img.src = url
  }

  // ── Thumbnail polling ────────────────────────────────────────────

  _pollForThumbnails(photoIds) {
    if (!this.pollThumbnailsUrlValue || !photoIds.length) return

    let pending = new Set(photoIds)
    let attempts = 0
    const maxAttempts = 25 // ~60s max

    const poll = async () => {
      if (pending.size === 0 || attempts >= maxAttempts) return
      attempts++

      try {
        const url = new URL(this.pollThumbnailsUrlValue, window.location.origin)
        pending.forEach(id => url.searchParams.append("ids[]", id))

        const res = await fetch(url, { headers: { "Accept": "application/json" } })
        const delay = attempts <= 3 ? 2000 : 3000
        if (!res.ok) {
          if (pending.size > 0) setTimeout(poll, delay)
          return
        }

        const data = await res.json()
        const photos = data.photos || {}

        for (const [idStr, info] of Object.entries(photos)) {
          if (!info.ready) continue
          const id = parseInt(idStr)

          // Update in-memory photo data
          const photo = this._allPhotos.find(p => p.id === id)
          if (photo) {
            if (info.thumbnail_url) photo.thumbnail_url = info.thumbnail_url
            if (info.optimized_url) photo.optimized_url = info.optimized_url
            if (info.original_url) photo.original_url = info.original_url
            // Server-side EXIF extraction may have filled capture metadata
            if (info.taken_at && !photo.taken_at) photo.taken_at = info.taken_at
            if (info.latitude != null && photo.latitude == null) {
              photo.latitude = info.latitude
              photo.longitude = info.longitude
            }
          }

          // Update tile background (preload to avoid flash)
          const tile = this.gridTarget.querySelector(`[data-photo-id="${id}"]`)
          if (tile && info.thumbnail_url && photo?.is_video) {
            this._addVideoStill(tile, info.thumbnail_url)
          } else if (tile && info.thumbnail_url) {
            const bg = tile.querySelector('[data-role="thumbnail-bg"]')
            if (bg) {
              const img = new Image()
              img.src = info.thumbnail_url
              img.onload = () => { bg.style.backgroundImage = `url(${info.thumbnail_url})` }
            }
          }

          // A video's still comes a little after the file itself
          // (PreparePhotoVideoJob): keep asking until it's there.
          if (photo?.is_video && !info.thumbnail_url) continue
          pending.delete(id)
        }
      } catch (e) {
        console.error("Thumbnail poll failed:", e)
      }

      if (pending.size > 0) {
        const delay = attempts <= 3 ? 2000 : 3000
        setTimeout(poll, delay)
      }
    }

    setTimeout(poll, 2000)
  }

  // ── Compression ───────────────────────────────────────────────────

  async _prepareFile(entry) {
    // Wait for HEIC→JPEG worker conversion before compressing
    if (entry._heicPromise) await entry._heicPromise
    const file = entry.file
    // Use pre-converted HEIC blob if available
    const source = entry.convertedBlob || file
    if (!this._isCompressibleImage(source)) {
      return { name: file.name, blob: source }
    }
    const blob = await this._compressImage(source)
    return { name: file.name, blob }
  }

  _isCompressibleImage(file) {
    return /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)
  }

  _compressImage(file, maxDim = 2000, quality = 0.80) {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        let { width, height } = img

        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => resolve(blob || file),
          "image/jpeg",
          quality
        )
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        resolve(file)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  // ── Filters ───────────────────────────────────────────────────────

  filterBySource() {
    this._resetAndReload()
  }

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => this._resetAndReload(), 300)
  }

  filterByDate() {
    // Custom date range from flatpickr inputs
    this._updateDateLabel()
    this._resetAndReload()
  }

  selectSort(e) {
    this._sort = e.currentTarget.dataset.sort || "recent"
    if (this.hasSortLabelTarget) this.sortLabelTarget.textContent = e.currentTarget.dataset.label
    this._saveSort(this._sort)
    this._resetAndReload()
  }

  // Persist the sort choice for this user (fire-and-forget; global preference).
  _saveSort(sort) {
    if (!this.hasSaveSortUrlValue) return
    fetch(this.saveSortUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ sort })
    }).catch(() => {})
  }

  selectDatePreset(e) {
    const preset = e.currentTarget.dataset.preset
    const label = e.currentTarget.dataset.label

    if (this.hasDateLabelTarget) this.dateLabelTarget.textContent = label

    this._applyPresetDates(preset)
    this._resetAndReload()
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = ""
    let to = ""

    if (preset === "10") {
      from = this._formatDateISO(new Date(today.getTime() - 10 * 86400000))
      to = this._formatDateISO(today)
    } else if (preset === "30") {
      from = this._formatDateISO(new Date(today.getTime() - 30 * 86400000))
      to = this._formatDateISO(today)
    } else if (preset === "90") {
      from = this._formatDateISO(new Date(today.getTime() - 90 * 86400000))
      to = this._formatDateISO(today)
    } else if (preset === "365") {
      from = this._formatDateISO(new Date(today.getTime() - 365 * 86400000))
      to = this._formatDateISO(today)
    } else if (preset === "ytd") {
      from = `${today.getFullYear()}-01-01`
      to = this._formatDateISO(today)
    }
    // else preset === "" means "All Dates" — clear both

    this._setDateInput(this.dateFromTarget, from)
    this._setDateInput(this.dateToTarget, to)
  }

  _setDateInput(target, value) {
    if (!target) return
    const fp = target._flatpickr
    if (fp) {
      if (value) fp.setDate(value, false); else fp.clear()
    } else {
      target.value = value
    }
  }

  _updateDateLabel() {
    if (!this.hasDateLabelTarget) return
    const from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    const to = this.hasDateToTarget ? this.dateToTarget.value : ""
    if (from || to) {
      const lang = document.documentElement.lang || "en"
      const fmt = { month: "short", day: "numeric" }
      const fromStr = from ? new Date(from + "T00:00:00").toLocaleDateString(lang, fmt) : "..."
      const toStr = to ? new Date(to + "T00:00:00").toLocaleDateString(lang, fmt) : "..."
      this.dateLabelTarget.textContent = `${fromStr} – ${toStr}`
    } else {
      this.dateLabelTarget.textContent = this.dateLabelTarget.dataset.default || "All Dates"
    }
  }

  _formatDateISO(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  }

  _resetAndReload() {
    this._page = 1
    this._isLastPage = false
    this._allPhotos = []
    this._weekPhotos = new Map()
    this._lastWeekKey = null
    if (this.hasGridTarget) this.gridTarget.innerHTML = ""
    if (this.hasSentinelTarget) this.sentinelTarget.classList.remove("hidden")
    this._loadPage(1)
  }

  // ── Bulk mode ─────────────────────────────────────────────────────

  toggleBulkMode() {
    this._bulkMode = !this._bulkMode
    this._selectedPhotos.clear()

    if (this.hasBulkBarTarget) {
      this.bulkBarTarget.classList.toggle("hidden", !this._bulkMode)
      if (this._bulkMode) this.bulkBarTarget.classList.add("flex")
      else this.bulkBarTarget.classList.remove("flex")
    }
    if (this.hasBulkToggleTarget) {
      this.bulkToggleTarget.classList.toggle("hidden", this._bulkMode)
    }

    // Toggle photo checkbox visibility
    this.gridTarget.querySelectorAll('input[data-photo-id]').forEach(cb => {
      const wrapper = cb.closest("div.absolute")
      if (wrapper) {
        if (this._bulkMode) {
          wrapper.classList.remove("hidden")
        } else {
          wrapper.classList.add("hidden")
          cb.checked = false
        }
      }
    })

    // Toggle week header checkboxes and pointer cursor
    this.gridTarget.querySelectorAll('[data-week-key]').forEach(header => {
      header.style.cursor = this._bulkMode ? "pointer" : ""
      const weekCb = header.querySelector('input[data-week-checkbox]')
      if (weekCb) {
        weekCb.style.display = this._bulkMode ? "" : "none"
        weekCb.checked = false
        weekCb.indeterminate = false
      }
    })

    // Reset Select All checkbox
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }

    this._updateBulkCount()
  }

  toggleSelect(e) {
    e.stopPropagation()
    const photoId = parseInt(e.target.dataset.photoId)
    this._updateSelection(photoId, e.target.checked)
  }

  _updateSelection(photoId, selected) {
    if (selected) {
      this._selectedPhotos.add(photoId)
    } else {
      this._selectedPhotos.delete(photoId)
    }
    this._updateBulkCount()
  }

  selectAll() {
    const shouldSelect = this.selectAllCheckboxTarget.checked

    for (const photo of this._allPhotos) {
      if (shouldSelect) this._selectedPhotos.add(photo.id)
      else this._selectedPhotos.delete(photo.id)
    }

    // Update all photo checkboxes
    this.gridTarget.querySelectorAll('input[data-photo-id]').forEach(cb => {
      cb.checked = shouldSelect
    })

    // Update all week checkboxes
    this.gridTarget.querySelectorAll('input[data-week-checkbox]').forEach(cb => {
      cb.checked = shouldSelect
      cb.indeterminate = false
    })

    this._updateBulkCount()
  }

  _toggleWeekSelect(weekKey) {
    const photoIds = this._weekPhotos.get(weekKey)
    if (!photoIds || photoIds.size === 0) return

    const cb = this.gridTarget.querySelector(`input[data-week-checkbox="${weekKey}"]`)
    const shouldSelect = cb?.checked ?? false

    for (const photoId of photoIds) {
      if (shouldSelect) this._selectedPhotos.add(photoId)
      else this._selectedPhotos.delete(photoId)

      const photoCb = this.gridTarget.querySelector(`input[data-photo-id="${photoId}"]`)
      if (photoCb) photoCb.checked = shouldSelect
    }

    this._updateBulkCount()
  }

  _updateBulkCount() {
    if (this.hasBulkCountTarget) {
      this.bulkCountTarget.textContent = `${this._selectedPhotos.size} selected`
    }

    // Enable/disable delete button based on whether any owned photos are selected
    if (this.hasDeleteBtnTarget) {
      const ownedCount = [...this._selectedPhotos].filter(id => this._ownedPhotoIds.has(id)).length
      const hasOwned = ownedCount > 0
      this.deleteBtnTarget.disabled = !hasOwned
      this.deleteBtnTarget.classList.toggle("opacity-30", !hasOwned)
      this.deleteBtnTarget.classList.toggle("cursor-not-allowed", !hasOwned)
      this.deleteBtnTarget.classList.toggle("cursor-pointer", hasOwned)
      this.deleteBtnTarget.title = hasOwned ? "Delete" : "You can only delete your own photos"
    }

    // Tri-state for Select All checkbox
    if (this.hasSelectAllCheckboxTarget) {
      const total = this._allPhotos.length
      const selected = this._selectedPhotos.size
      this.selectAllCheckboxTarget.checked = total > 0 && selected === total
      this.selectAllCheckboxTarget.indeterminate = selected > 0 && selected < total
    }

    // Tri-state for each week checkbox
    for (const [weekKey, photoIds] of this._weekPhotos) {
      const cb = this.gridTarget.querySelector(`input[data-week-checkbox="${weekKey}"]`)
      if (!cb) continue
      const weekSelected = [...photoIds].filter(id => this._selectedPhotos.has(id)).length
      cb.checked = weekSelected > 0 && weekSelected === photoIds.size
      cb.indeterminate = weekSelected > 0 && weekSelected < photoIds.size
    }
  }

  bulkDelete() {
    if (this._selectedPhotos.size === 0) return
    // Only owned photos can be deleted
    const deletableIds = [...this._selectedPhotos].filter(id => this._ownedPhotoIds.has(id))
    if (deletableIds.length === 0) return

    const total = this._selectedPhotos.size
    let message
    if (deletableIds.length === total) {
      message = deletableIds.length === 1
        ? "Are you sure you want to delete this photo?"
        : `Are you sure you want to delete ${deletableIds.length} photos?`
    } else {
      message = `${deletableIds.length} of ${total} selected photos are yours and will be deleted. Photos uploaded by others will be skipped.`
    }
    this.deleteConfirmMessageTarget.textContent = message
    this._pendingDeleteIds = deletableIds
    this.deleteConfirmModalTarget.classList.remove("hidden")
  }

  cancelDelete() {
    this.deleteConfirmModalTarget.classList.add("hidden")
  }

  async confirmDelete() {
    this.deleteConfirmModalTarget.classList.add("hidden")
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const idsToDelete = this._pendingDeleteIds || [...this._selectedPhotos]

    for (const photoId of idsToDelete) {
      try {
        await fetch(`${this.baseUrlValue}/${photoId}`, {
          method: "DELETE",
          headers: { "X-CSRF-Token": token, "Accept": "application/json" }
        })
        this._removeTile(photoId)
        this._ownedPhotoIds.delete(photoId)
      } catch (e) {
        console.error(`Failed to delete photo ${photoId}:`, e)
      }
    }

    this._pendingDeleteIds = null
    this._selectedPhotos.clear()
    this.toggleBulkMode()
  }

  async bulkDownload() {
    if (this._selectedPhotos.size === 0) return

    // Show spinner, lock widths to prevent layout shift
    const btn = this.downloadBtnTarget
    const bar = this.bulkBarTarget
    const contentEl = this.downloadBtnContentTarget
    const spinnerEl = this.downloadBtnSpinnerTarget
    bar.style.minWidth = `${bar.offsetWidth}px`
    btn.style.minWidth = `${btn.offsetWidth}px`
    btn.disabled = true
    contentEl.style.display = "none"
    spinnerEl.style.display = ""
    spinnerEl.classList.remove("hidden")

    try {
      const { default: JSZip } = await import("jszip")
      const zip = new JSZip()
      const usedNames = new Set()

      // Build folder/zip name from project number + name
      const num = this.projectNumberValue
      const pName = this.projectNameValue
      const folderLabel = num && pName ? `${num} - ${pName}` : (num || pName || "Photos")
      const folder = zip.folder(folderLabel)

      for (const photoId of this._selectedPhotos) {
        const photo = this._allPhotos.find(p => p.id === photoId)
        if (!photo || !photo.original_url) continue

        try {
          const resp = await fetch(photo.original_url)
          if (!resp.ok) continue
          const blob = await resp.blob()

          let name = photo.filename || `photo_${photoId}.jpg`
          if (usedNames.has(name)) {
            const dot = name.lastIndexOf(".")
            const base = dot > 0 ? name.slice(0, dot) : name
            const ext = dot > 0 ? name.slice(dot) : ""
            name = `${base}_${photoId}${ext}`
          }
          usedNames.add(name)
          folder.file(name, blob)
        } catch (e) {
          console.error(`Failed to fetch photo ${photoId}:`, e)
        }
      }

      const content = await zip.generateAsync({ type: "blob" })
      const url = URL.createObjectURL(content)
      const a = document.createElement("a")
      a.href = url
      a.download = `${folderLabel}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      // Restore button
      contentEl.style.display = ""
      spinnerEl.style.display = "none"
      btn.disabled = false
      btn.style.minWidth = ""
      bar.style.minWidth = ""
    }

    this.toggleBulkMode()
  }

  // ── Delete single photo (called from lightbox) ────────────────────

  removePhotoFromGrid(photoId) {
    this._removeTile(photoId)
  }

  _removeTile(photoId) {
    const tile = this.gridTarget.querySelector(`[data-photo-id="${photoId}"]`)
    if (tile) tile.remove()
    this._allPhotos = this._allPhotos.filter(p => p.id !== photoId)

    // Remove from week tracking
    for (const [, ids] of this._weekPhotos) {
      ids.delete(photoId)
    }

    if (this.hasPhotoCountTarget) {
      const current = parseInt(this.photoCountTarget.textContent) || 0
      this.photoCountTarget.textContent = Math.max(0, current - 1)
    }
  }

  // Update a photo tile's thumbnail (called after rotate)
  updatePhotoTile(photoId, data) {
    const tile = this.gridTarget.querySelector(`[data-photo-id="${photoId}"]`)
    if (tile) {
      const bg = tile.querySelector('[data-role="thumbnail-bg"]')
      if (bg) {
        if (data.thumbnail_url) bg.style.backgroundImage = `url(${data.thumbnail_url})`
        if (data.rotation !== undefined) bg.style.transform = data.rotation ? `rotate(${data.rotation}deg) scale(1.2)` : ""
      }
      if (data.comment_count !== undefined) {
        const existingBadge = tile.querySelector('[data-role="comment-badge"]')
        if (data.comment_count > 0) {
          if (existingBadge) {
            existingBadge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" /></svg>${data.comment_count}`
          } else {
            const overlays = tile.querySelector('[data-role="thumbnail-bg"]')?.parentElement
            if (overlays) {
              const badge = document.createElement("div")
              badge.setAttribute("data-role", "comment-badge")
              badge.className = "absolute bottom-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/60 text-white text-xs rounded"
              badge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" /></svg>${data.comment_count}`
              overlays.appendChild(badge)
            }
          }
        } else if (existingBadge) {
          existingBadge.remove()
        }
      }
      if (data.has_annotations && !tile.querySelector('[data-role="annotation-badge"]')) {
        const overlays = tile.querySelector('[data-role="thumbnail-bg"]')?.parentElement
        if (overlays) {
          const annBadge = document.createElement("div")
          annBadge.setAttribute("data-role", "annotation-badge")
          annBadge.className = "absolute bottom-1.5 right-1.5 p-1 bg-brand/80 text-white rounded"
          annBadge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3"><path stroke-linecap="round" stroke-linejoin="round" d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128Zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42"/></svg>`
          overlays.appendChild(annBadge)
        }
      }
    }
    const photo = this._allPhotos.find(p => p.id === photoId)
    if (photo) {
      if (data.thumbnail_url) photo.thumbnail_url = data.thumbnail_url
      if (data.optimized_url) photo.optimized_url = data.optimized_url
      if (data.original_url) photo.original_url = data.original_url
      if (data.comment_count !== undefined) photo.comment_count = data.comment_count
      if (data.has_annotations !== undefined) photo.has_annotations = data.has_annotations
      if (data.rotation !== undefined) photo.rotation = data.rotation
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────

  _formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  _formatDuration(seconds) {
    const total = Math.max(0, Math.round(Number(seconds) || 0))
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    const pad = (n) => String(n).padStart(2, "0")
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
  }
}
